import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, CalendarClock } from "lucide-react";
import { Reveal } from "./Motion";
import { useLanguage } from "../i18n/LanguageContext";

const CountdownCard = () => {
  const { t } = useLanguage();
  const days = Math.max(0, Math.ceil((new Date(2027, 0, 20) - new Date()) / 86400000));
  return (
    <Reveal delay={0.22} className="mt-10">
      <div
        data-testid="regulation-page-countdown"
        className="flex max-w-xl flex-wrap items-center gap-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg"
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-navy">
          <CalendarClock className="h-6 w-6 text-[#E8912D]" />
        </div>
        <div>
          <p className="font-display text-4xl font-bold tracking-tight text-navy">
            {days}
            <span className="ml-3 text-lg font-semibold text-slate-400">20.01.2027</span>
          </p>
          <p className="mt-1 font-mono text-[0.62rem] uppercase tracking-[0.22em] text-slate-400">
            {t.home.regDays}
          </p>
        </div>
      </div>
    </Reveal>
  );
};

export const PageHero = ({ overline, title, lead, image, countdown = false }) => (
  <section className="relative overflow-hidden bg-paper pt-32 pb-14 md:pt-44 md:pb-20">
    <div className="blueprint-grid absolute inset-0 opacity-60" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal>
        <p className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-accent">
          {overline}
        </p>
      </Reveal>
      <Reveal delay={0.08}>
        <h1 className="mt-5 max-w-3xl font-display text-4xl font-bold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl">
          {title}
        </h1>
      </Reveal>
      <Reveal delay={0.16}>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">{lead}</p>
      </Reveal>
      {countdown && <CountdownCard />}
      {image && (
        <Reveal delay={0.24} className="mt-12">
          <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-xl">
            <img src={image} alt={title} className="h-64 w-full object-cover md:h-96" loading="lazy" />
          </div>
        </Reveal>
      )}
    </div>
  </section>
);

export const ContentSection = ({ section, index }) => (
  <Reveal delay={0.05 * (index % 3)}>
    <section className="border-t border-slate-200 py-12 md:py-16" data-testid={`content-section-${index}`}>
      <div className="grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <span className="font-mono text-[0.68rem] tracking-[0.3em] text-slate-400">
            {String(index + 1).padStart(2, "0")}
          </span>
          <h2 className="mt-2 font-display text-xl font-semibold tracking-tight text-ink md:text-2xl">
            {section.heading}
          </h2>
        </div>
        <div className="lg:col-span-8">
          {section.paragraph && (
            <p className="text-base leading-relaxed text-slate-600 md:text-lg">{section.paragraph}</p>
          )}
          {section.list && (
            <ul className={`space-y-3 ${section.paragraph ? "mt-6" : ""}`}>
              {section.list.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-base leading-relaxed text-slate-600">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-accent" strokeWidth={3} />
                  {item}
                </li>
              ))}
            </ul>
          )}
          {section.link && (
            <a
              href={section.link.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`section-link-${index}`}
              className="mt-6 inline-flex items-center gap-2 font-semibold text-navy transition-colors duration-300 hover:text-accent"
            >
              {section.link.label}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          )}
          {section.image && (
            <div className="mt-8 overflow-hidden rounded-xl border border-slate-200 shadow-lg">
              <img src={section.image} alt={section.heading} className="h-56 w-full object-cover md:h-72" loading="lazy" />
            </div>
          )}
        </div>
      </div>
    </section>
  </Reveal>
);

export const PageCtaBand = ({ cta }) => {
  const { t } = useLanguage();
  if (!cta) return null;
  return (
    <Reveal>
      <div className="mt-8 rounded-2xl bg-navy px-8 py-10 md:px-12 md:py-14" data-testid="page-cta-band">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <h3 className="max-w-xl font-display text-2xl font-semibold tracking-tight text-white md:text-3xl">
            {t.home.ctaTitle}
          </h3>
          <Link
            to={cta.to}
            data-testid="page-cta-button"
            className="group flex shrink-0 items-center gap-2 rounded-full bg-white px-6 py-3.5 font-semibold text-navy transition-colors duration-300 hover:bg-slate-100"
          >
            {cta.label}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </Reveal>
  );
};

const ContentPage = ({ content, heroImage, countdown = false }) => (
  <main data-testid="content-page">
    <PageHero overline={content.overline} title={content.title} lead={content.lead} image={heroImage} countdown={countdown} />
    <div className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
      {content.sections.map((s, i) => (
        <ContentSection key={i} section={s} index={i} />
      ))}
      <PageCtaBand cta={content.cta} />
    </div>
  </main>
);

export default ContentPage;
